/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Fragment, useState, useEffect, useCallback } from 'react'
import { useIntl } from 'react-intl'

import {
  EuiButton,
  EuiButtonEmpty,
  EuiCopy,
  EuiContextMenuItem,
  EuiFieldText,
  EuiIcon,
  EuiModal,
  EuiModalBody,
  EuiModalFooter,
  EuiModalHeader,
  EuiModalHeaderTitle,
  EuiOverlayMask,
  EuiTextColor,
  EuiButtonIcon,
  EuiCallOut,
  EuiFormRow,
  EuiToolTip,
  useGeneratedHtmlId,
  EuiCode,
  EuiSpacer,
  EuiText,
  EuiFlexGroup,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import genericMessages from '@modules/project-lib/genericMessages'
import usePermissions from '@modules/permissions-lib/hooks'
import type { AnyProject } from '@modules/project-api/types'
import { getIdPrefix } from '@modules/utils/idPrefix'
import type { UseListProjectsQueryHook } from '@modules/project-components/ProjectCps/ProjectCpsContext'
import { addToast } from '@modules/cui/Toasts'

import { useIsCpsFeatureActive } from '../ProjectCps/hooks'

export interface DeleteProjectButtonProps {
  project: AnyProject
  hideConfirmationModalOnSubmit?: boolean
  onDelete: () => unknown
  onComplete: () => void
  isLoading: boolean
  isDisabled?: boolean
  useListElasticsearchProjectsQuery: UseListProjectsQueryHook
  useListObservabilityProjectsQuery: UseListProjectsQueryHook
  useListSecurityProjectsQuery: UseListProjectsQueryHook
}

export const addDeletedProjectToast = () => {
  addToast({
    family: 'project-was-deleted',
    color: 'primary',
    iconType: 'broom',
    title: <FormattedMessage id='toasts.project-deleted.title' defaultMessage='Project deleted' />,
    text: (
      <FormattedMessage
        id='toasts.project-deleted.text'
        defaultMessage='The project has been deleted.'
      />
    ),
  })
}

const DeleteProjectButton: React.FunctionComponent<DeleteProjectButtonProps> = ({
  project,
  hideConfirmationModalOnSubmit,
  onDelete,
  onComplete,
  isLoading,
  isDisabled: isDisabledByInvoker,
  useListElasticsearchProjectsQuery,
  useListObservabilityProjectsQuery,
  useListSecurityProjectsQuery,
}) => {
  const { hasPermissions: hasDeletePermission } = usePermissions([
    { type: `project-${project.type}`, action: 'delete', id: project.id },
  ])
  const [isConfirming, setIsConfirming] = useState(false)
  const [pristine, setPristine] = useState(true)
  const [enteredProjectName, setEnteredProjectName] = useState<string>()
  const { formatMessage } = useIntl()
  const modalTitleId = useGeneratedHtmlId()

  const isCpsEnabled = useIsCpsFeatureActive()
  const shouldFetchLinkedProjects = isConfirming && isCpsEnabled

  // For elasticsearch projects, only check elasticsearch linked projects
  const shouldFetchElasticsearch = shouldFetchLinkedProjects && project.type === 'elasticsearch'
  // For observability and security, check both since they can be linked together
  const shouldFetchObservabilityAndSecurity =
    shouldFetchLinkedProjects && (project.type === 'observability' || project.type === 'security')

  const elasticsearchProjectsQuery = useListElasticsearchProjectsQuery(
    { linked: project.id },
    { enabled: shouldFetchElasticsearch },
  )
  const observabilityProjectsQuery = useListObservabilityProjectsQuery(
    { linked: project.id },
    { enabled: shouldFetchObservabilityAndSecurity },
  )
  const securityProjectsQuery = useListSecurityProjectsQuery(
    { linked: project.id },
    { enabled: shouldFetchObservabilityAndSecurity },
  )

  const isCheckingLinkedProjects =
    elasticsearchProjectsQuery.isFetching ||
    observabilityProjectsQuery.isFetching ||
    securityProjectsQuery.isFetching

  const linkedProjectsCount =
    (elasticsearchProjectsQuery.data?.items.length || 0) +
    (observabilityProjectsQuery.data?.items.length || 0) +
    (securityProjectsQuery.data?.items.length || 0)

  const isNameConfirmed = enteredProjectName?.trim() === project.name.trim()
  const isDisabled = isDisabledByInvoker || !hasDeletePermission
  const color = isDisabled ? 'disabled' : 'danger'

  const submitDelete = useCallback(() => {
    setPristine(false)

    if (isNameConfirmed) {
      onDelete()
    }

    if (hideConfirmationModalOnSubmit) {
      setIsConfirming(false)
    }
  }, [isNameConfirmed, onDelete, hideConfirmationModalOnSubmit])

  useEffect(() => {
    setPristine(true)
  }, [isConfirming])

  useEffect(() => {
    if (enteredProjectName !== undefined) {
      setPristine(false)
    }
  }, [enteredProjectName])

  return (
    <Fragment>
      {renderButton()}
      {isConfirming && renderModal()}
    </Fragment>
  )

  function renderButton() {
    return (
      <EuiContextMenuItem
        key='delete'
        icon={<EuiIcon type='trash' size='m' color={color} />}
        onClick={openModal}
        disabled={isDisabled}
        data-test-id='delete-project-link'
      >
        <EuiTextColor color={color}>
          <FormattedMessage id='project.delete.button' defaultMessage='Delete project' />
        </EuiTextColor>
      </EuiContextMenuItem>
    )
  }

  function renderModal() {
    const copyToClipboardMessage = formatMessage({
      id: 'project.copyToClipboard.body',
      defaultMessage: 'Copy project name to clipboard',
    })

    return (
      <EuiOverlayMask>
        <EuiModal aria-labelledby={modalTitleId} onClose={closeModal} role='alertdialog'>
          <EuiModalHeader>
            <EuiModalHeaderTitle id={modalTitleId}>
              <FormattedMessage
                id='project.delete.title'
                defaultMessage='Delete {name} project?'
                values={{
                  name: project.name,
                }}
              />
            </EuiModalHeaderTitle>
          </EuiModalHeader>
          <EuiModalBody style={{ minHeight: '180px' }}>
            <EuiCallOut
              heading='p'
              size='s'
              title={
                <FormattedMessage
                  id='project.delete.confirmation.callout'
                  defaultMessage='This action cannot be undone and permanently deletes the {name} ( ID {idPrefix}) and all related with that project data.'
                  values={{
                    name: (
                      <EuiTextColor color='accent'>
                        {project.name}{' '}
                        <EuiCopy textToCopy={project.name}>
                          {(copy) => (
                            <EuiToolTip
                              content={copyToClipboardMessage}
                              disableScreenReaderOutput={true}
                            >
                              <EuiButtonIcon
                                aria-label={copyToClipboardMessage}
                                iconType='copy'
                                color='accent'
                                size='xs'
                                onClick={copy}
                              />
                            </EuiToolTip>
                          )}
                        </EuiCopy>
                      </EuiTextColor>
                    ),
                    idPrefix: <EuiCode color='warning'>{getIdPrefix(project)}</EuiCode>,
                  }}
                />
              }
              color='warning'
              iconType='warning'
            >
              {!isCheckingLinkedProjects && linkedProjectsCount > 0 && (
                <EuiFlexGroup gutterSize='s' alignItems='center'>
                  <EuiText color='warning' size='s' component='span'>
                    <FormattedMessage
                      id='project.delete.linked.warning'
                      defaultMessage="{projectName} is a linked project in another project’s cross-project search configuration. Cross-project searches relying on this project's data might return incomplete results, or fail."
                      values={{
                        projectName: <EuiTextColor color='accent'>{project.name}</EuiTextColor>,
                      }}
                    />
                  </EuiText>
                </EuiFlexGroup>
              )}
            </EuiCallOut>
            <EuiSpacer size='m' />
            <EuiFormRow
              hasEmptyLabelSpace={true}
              label={
                <FormattedMessage
                  id='project.delete.label'
                  defaultMessage='Type the project name to confirm'
                />
              }
              error={
                <FormattedMessage
                  id='project.delete.error'
                  defaultMessage='The project name you typed does not match {name}'
                  values={{
                    name: <strong>{project.name}</strong>,
                  }}
                />
              }
              isInvalid={!pristine && !isNameConfirmed}
              fullWidth={true}
            >
              <EuiFieldText
                name='confirmation-input'
                placeholder={project.name}
                value={enteredProjectName ?? ''}
                onChange={(e) => setEnteredProjectName(e.target.value)}
                isInvalid={!pristine && !isNameConfirmed}
                fullWidth={true}
                data-test-id='delete-project-modal-input'
              />
            </EuiFormRow>
          </EuiModalBody>

          <EuiModalFooter>
            {!isLoading && (
              <EuiButtonEmpty onClick={closeModal} data-test-id='delete-project-modal-cancel'>
                <FormattedMessage {...genericMessages.cancel} />
              </EuiButtonEmpty>
            )}

            <EuiButton
              onClick={submitDelete}
              fill={true}
              color='danger'
              isLoading={isLoading}
              data-test-id='delete-project-modal-confirm'
            >
              <FormattedMessage
                {...(isLoading ? genericMessages.deleting : genericMessages.delete)}
              />
            </EuiButton>
          </EuiModalFooter>
        </EuiModal>
      </EuiOverlayMask>
    )
  }

  function openModal() {
    setIsConfirming(true)
  }

  function closeModal() {
    setIsConfirming(false)
    setEnteredProjectName(undefined)
    onComplete()
  }
}

export default DeleteProjectButton
